import React, { useState, useMemo } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle2, XCircle, Volume2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSounds } from '@/hooks/useSounds';

export default function MultipleChoice({ 
  question, 
  options, 
  correctAnswer, 
  explanation,
  onAnswer,
  disabled = false 
}) {
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const { playCorrect, playIncorrect, playClick } = useSounds();

  // Normalize correctAnswer to index
  // correctAnswer can be: index (0,1,2), string ('Do'), or the actual option text
  const correctIndex = useMemo(() => {
    if (typeof correctAnswer === 'number') {
      return correctAnswer;
    }
    // If it's a string, find the matching option index
    const index = options.findIndex(opt => 
      opt === correctAnswer || 
      opt.toLowerCase() === String(correctAnswer).toLowerCase() ||
      opt.trim() === String(correctAnswer).trim()
    );
    return index >= 0 ? index : 0;
  }, [correctAnswer, options]);

  const handleSelect = (index) => {
    if (isAnswered || disabled) return;
    
    playClick();
    setSelectedAnswer(index);
    const correct = index === correctIndex;
    setIsCorrect(correct);
    setIsAnswered(true);
    
    // Ses efekti çal
    setTimeout(() => {
      if (correct) {
        playCorrect();
      } else {
        playIncorrect();
      }
    }, 200);
    
    setTimeout(() => {
      onAnswer?.(correct, index);
    }, 1500);
  };

  const getOptionStyle = (index) => {
    if (!isAnswered) {
      return selectedAnswer === index 
        ? 'border-blue-500 bg-blue-50' 
        : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50';
    }
    
    if (index === correctIndex) {
      return 'border-green-500 bg-green-50';
    }
    
    if (selectedAnswer === index && !isCorrect) {
      return 'border-red-500 bg-red-50';
    }
    
    return 'border-gray-200 opacity-50';
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Question */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-800 mb-2">
          {question}
        </h2>
      </div>

      {/* Options */}
      <div className="space-y-3">
        {options.map((option, index) => (
          <button
            key={index}
            onClick={() => handleSelect(index)}
            disabled={isAnswered || disabled}
            className={cn(
              'w-full p-4 rounded-xl border-2 text-left transition-all duration-200',
              'flex items-center gap-3',
              getOptionStyle(index)
            )}
          >
            <div className={cn(
              'w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm',
              isAnswered && index === correctIndex ? 'bg-green-500 text-white' :
              isAnswered && selectedAnswer === index && !isCorrect ? 'bg-red-500 text-white' :
              'bg-gray-100 text-gray-600'
            )}>
              {isAnswered && index === correctIndex ? (
                <CheckCircle2 className="w-5 h-5" />
              ) : isAnswered && selectedAnswer === index && !isCorrect ? (
                <XCircle className="w-5 h-5" />
              ) : (
                String.fromCharCode(65 + index)
              )}
            </div>
            <span className={cn(
              'flex-1 font-medium',
              isAnswered && index === correctIndex ? 'text-green-700' :
              isAnswered && selectedAnswer === index && !isCorrect ? 'text-red-700' :
              'text-gray-700'
            )}>
              {option}
            </span>
          </button>
        ))}
      </div>

      {/* Feedback */}
      {isAnswered && (
        <div className={cn(
          'mt-6 p-4 rounded-xl',
          isCorrect ? 'bg-green-100 border border-green-200' : 'bg-red-100 border border-red-200'
        )}>
          <div className="flex items-center gap-2 mb-2">
            {isCorrect ? (
              <>
                <CheckCircle2 className="w-6 h-6 text-green-600" />
                <span className="font-bold text-green-700">Harika! Doğru cevap!</span>
              </>
            ) : (
              <>
                <XCircle className="w-6 h-6 text-red-600" />
                <span className="font-bold text-red-700">Yanlış cevap</span>
              </>
            )}
          </div>
          {explanation && (
            <p className={cn(
              'text-sm',
              isCorrect ? 'text-green-600' : 'text-red-600'
            )}>
              {explanation}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
